<template>
  <div class="contract-modern">
    <NavBar />
    <AuthBySessionExpired />

    <div class="contract-container">
      <!-- Header Section -->
      <div class="contract-header">
        <div class="header-content">
          <div class="back-button-section">
            <v-btn class="btn-back-modern" @click="handleToBack">
              <i class="fas fa-arrow-left mr-2"></i>
              Voltar
            </v-btn>
          </div>

          <div class="title-section">
            <h1 class="page-title">
              <i class="fas fa-file-contract title-icon"></i>
              Editar Contrato
            </h1>
            <p class="page-subtitle">
              Gerencie e edite contratos dos residentes
            </p>
          </div>
        </div>
      </div>

      <!-- Content Section -->
      <div class="contract-content">
        <div v-if="resident && contract" class="content-wrapper">
          <!-- Resident and Contract Info Card -->
          <div class="info-card">
            <div class="card-header">
              <h3 class="card-title">
                <i class="fas fa-info-circle mr-2"></i>
                Informações do Contrato
              </h3>
            </div>
            <div class="card-content">
              <div class="info-grid">
                <div class="info-item">
                  <label class="info-label">Residente</label>
                  <p class="info-value">{{ resident.name }}</p>
                </div>
                <div class="info-item">
                  <label class="info-label">Nome do Contrato</label>
                  <p class="info-value">{{ contract.name }}</p>
                </div>
                <div class="info-item">
                  <label class="info-label">Status</label>
                  <div class="switch-container">
                    <label class="switch-modern">
                      <input
                        type="checkbox"
                        v-model="contract.active"
                        :disabled="loading"
                        @change="update({ reload: false })"
                      />
                      <span class="slider-modern"> </span>
                    </label>
                    <span
                      class="status-text"
                      :class="{ active: contract.active }"
                    >
                      {{ contract.active ? "Ativo" : "Inativo" }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Contract Variables Card -->
          <div class="variables-card">
            <div class="card-header">
              <h3 class="card-title">
                <i class="fas fa-code mr-2"></i>
                Variáveis Disponíveis
              </h3>
            </div>
            <div class="card-content">
              <ContractVariableList />
            </div>
          </div>

          <!-- Contract Name Form -->
          <div class="form-card">
            <div class="card-header">
              <h3 class="card-title">
                <i class="fas fa-edit mr-2"></i>
                Nome do Contrato
              </h3>
            </div>
            <div class="card-content">
              <div class="form-group">
                <LabelRequiredField label="Nome do contrato" />
                <div class="modern-input-container">
                  <input
                    type="text"
                    v-model="contract.name"
                    placeholder="Digite o nome do contrato"
                    class="modern-form-input"
                    :disabled="loading"
                  />
                  <i class="fas fa-signature input-icon"></i>
                </div>
              </div>
            </div>
          </div>

          <!-- Contract Text Form -->
          <div class="text-card">
            <div class="card-header">
              <h3 class="card-title">
                <i class="fas fa-file-alt mr-2"></i>
                Conteúdo do Contrato
              </h3>
            </div>
            <div class="card-content">
              <div class="form-group">
                <div class="textarea-container">
                  <textarea
                    id="contractText"
                    v-model="contract.text"
                    placeholder="Digite o conteúdo do contrato aqui..."
                    class="modern-textarea"
                    :disabled="loading"
                    rows="12"
                  ></textarea>
                  <div class="textarea-footer">
                    <span class="char-counter">
                      {{ contract.text ? contract.text.length : 0 }}/5000
                      caracteres
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Error Messages -->
          <div v-if="errors.length > 0" class="errors-card">
            <ErrorsMessages :errors="errors" />
          </div>

          <!-- Action Buttons -->
          <div class="actions-section">
            <v-btn
              class="btn-save-modern"
              @click="update({ reload: true })"
              :disabled="loading"
            >
              <i class="fas fa-save mr-2"></i>
              <span v-if="loading">Salvando...</span>
              <span v-else>Salvar Contrato</span>
            </v-btn>
          </div>
        </div>

        <!-- Not Found State -->
        <div v-else class="not-found-card">
          <div class="not-found-content">
            <i class="fas fa-exclamation-triangle not-found-icon"></i>
            <h3 class="not-found-title">Informações não encontradas</h3>
            <p class="not-found-text">
              Não foi possível carregar os dados do contrato ou residente
            </p>
            <v-btn class="btn-back-modern" @click="handleToBack">
              <i class="fas fa-arrow-left mr-2"></i>
              Voltar
            </v-btn>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NavBar from "../../templates/NavBar";
import ContractVariableList from "./ContractVariableList.vue";
import LabelRequiredField from "../../templates/Components/LabelRequiredField";
import toast from "../../lib/ToastNotification";
import AuthBySessionExpired from "../../templates/Components/AuthBySessionExpired";
import ErrorsMessages from "../../templates/Components/ErrorsMessages";
import api from "../../services/api";
import routes from "../../routes";

export default {
  name: "ContractUpdate",
  components: {
    NavBar,
    AuthBySessionExpired,
    LabelRequiredField,
    ErrorsMessages,
    ContractVariableList,
  },
  data() {
    return {
      loading: false,
      contract: [],
      resident: [],
      contract_variables: [],
      errors: [],
      contract_id: null,
      resident_id: null,
    };
  },
  async created() {
    if (this.$route.params.contract_id && this.$route.params.resident_id) {
      this.contract_id = this.$route.params.contract_id;
      this.resident_id = this.$route.params.resident_id;
    }

    await this.getContractVariables();
    await this.getContractById();
    await this.getResidentById();
  },
  methods: {
    async getContractVariables() {
      try {
        this.loading = true;
        const response = await api.get("/contract/contract-variables");
        const { contract_variables } = response.data;
        this.contract_variables = contract_variables;
        this.loading = false;
      } catch (error) {
        this.loading = false;
        console.log(error);
      }
    },
    async getContractById() {
      try {
        this.loading = true;
        const { contract_id } = this;
        let params = { contract_id };
        const response = await api.get("/contract", { params });
        const { error, errors, contract } = response.data;

        if (!error) {
          this.errors = [];
          this.contract = contract;
        } else {
          this.errors = errors;
        }
        this.loading = false;
      } catch (error) {
        this.loading = false;
        console.log(error);
      }
    },
    async getResidentById() {
      try {
        this.loading = true;
        const { resident_id } = this;
        const response = await api.get("/rest-home/resident/path", {
          params: { id: resident_id },
        });
        const { resident, error } = response.data;

        if (!error) {
          this.resident = resident;
        }
        this.loading = false;
      } catch (error) {
        this.loading = false;
        console.log(error);
      }
    },
    async update({ reload = true }) {
      try {
        this.loading = true;
        const { resident_id, contract_id } = this;
        const name = this.contract.name;
        const text = this.contract.text;
        const active = this.contract.active;

        let params = {
          resident_id,
          contract_id,
          name,
          text,
          active,
        };

        const response = await api.put("/contract", params);
        const { error, errors, message } = response.data;

        if (!error) {
          toast(message, "success");
          if (reload) {
            this.handleToBack();
          }
        } else {
          this.errors = errors;
        }
        this.loading = false;
      } catch (error) {
        this.loading = false;
        console.log(error);
      }
    },
    handleToBack: () => routes.go(-1),
  },
};
</script>

<style scoped>
.contract-modern {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}

.contract-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Header Section */
.contract-header {
  padding: 24px 0;
  background: white;
  border-radius: 0 0 24px 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 32px;
}

.header-content {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 24px;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.title-icon {
  color: #0f65b0;
  margin-right: 12px;
}

.page-subtitle {
  color: #64748b;
  margin: 4px 0 0 0;
  font-size: 16px;
}

/* Buttons */
.btn-back-modern {
  background: transparent !important;
  color: #64748b !important;
  border: 2px solid #e5e7eb !important;
  border-radius: 12px !important;
  padding: 12px 20px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
  transition: all 0.3s ease !important;
}

.btn-back-modern:hover {
  background: #f8fafc !important;
  border-color: #cbd5e1 !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

.btn-save-modern {
  background: linear-gradient(135deg, #1e884d 0%, #16a34a 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 14px 32px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  font-size: 16px !important;
  box-shadow: 0 4px 15px rgba(30, 136, 77, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-save-modern:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(30, 136, 77, 0.4) !important;
}

.btn-save-modern:disabled {
  opacity: 0.7 !important;
  cursor: not-allowed !important;
  transform: none !important;
}

/* Content Layout */
.contract-content {
  margin-bottom: 32px;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Card Components */
.info-card,
.variables-card,
.form-card,
.text-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.card-header {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.card-title i {
  color: #0f65b0;
}

.card-content {
  padding: 24px;
}

/* Info Grid */
.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.info-label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
}

.info-value {
  font-size: 16px;
  font-weight: 500;
  color: #1e293b;
  margin: 0;
}

/* Switch Component */
.switch-container {
  display: flex;
  align-items: center;
  gap: 12px;
}

.switch-modern {
  position: relative;
  display: inline-block;
  width: 64px;
  height: 34px;
  cursor: pointer;
}

.switch-modern input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider-modern {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  transition: 0.4s;
  border-radius: 34px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.slider-modern:before {
  position: absolute;
  content: "";
  height: 26px;
  width: 26px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.switch-modern input:checked + .slider-modern {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.switch-modern input:checked + .slider-modern:before {
  transform: translateX(30px);
}

.switch-modern input:disabled + .slider-modern {
  opacity: 0.5;
  cursor: not-allowed;
}

.thumb-icon {
  font-size: 10px;
  color: #d1d5db;
  transition: 0.3s;
}

.switch-modern input:checked + .slider-modern .thumb-icon {
  color: #10b981;
}

.status-text {
  font-size: 14px;
  font-weight: 600;
  color: #ef4444;
  transition: color 0.3s ease;
}

.status-text.active {
  color: #10b981;
}

/* Form Components */
.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.modern-input-container {
  position: relative;
  display: flex;
  align-items: center;
}

.modern-form-input {
  width: 100%;
  height: 48px;
  padding: 12px 48px 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 16px;
  font-family: "WorkSans-Regular", sans-serif;
  transition: all 0.3s ease;
  background: white;
}

.modern-form-input:focus {
  outline: none;
  border-color: #0f65b0;
  box-shadow: 0 0 0 3px rgba(15, 101, 176, 0.1);
}

.modern-form-input:disabled {
  background: #f8fafc;
  color: #9ca3af;
  cursor: not-allowed;
}

.input-icon {
  position: absolute;
  right: 16px;
  color: #9ca3af;
  font-size: 16px;
  pointer-events: none;
}

/* Textarea Component */
.textarea-container {
  position: relative;
}

.modern-textarea {
  width: 100%;
  min-height: 300px;
  padding: 16px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 15px;
  font-family: "WorkSans-Regular", sans-serif;
  line-height: 1.6;
  resize: vertical;
  transition: all 0.3s ease;
  background: white;
}

.modern-textarea:focus {
  outline: none;
  border-color: #0f65b0;
  box-shadow: 0 0 0 3px rgba(15, 101, 176, 0.1);
}

.modern-textarea:disabled {
  background: #f8fafc;
  color: #9ca3af;
  cursor: not-allowed;
}

.textarea-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

.char-counter {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
}

/* Error Card */
.errors-card {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 1px solid #fca5a5;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 4px 15px rgba(239, 68, 68, 0.1);
}

/* Actions Section */
.actions-section {
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
}

/* Not Found State */
.not-found-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  padding: 60px 40px;
  text-align: center;
}

.not-found-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.not-found-icon {
  font-size: 64px;
  color: #f59e0b;
  margin-bottom: 8px;
}

.not-found-title {
  font-size: 24px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.not-found-text {
  font-size: 16px;
  color: #64748b;
  margin: 0 0 24px 0;
  max-width: 400px;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .header-content {
    grid-template-columns: 1fr;
    gap: 16px;
    text-align: center;
  }

  .page-title {
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .contract-container {
    padding: 0 16px;
  }

  .contract-header {
    padding: 20px 0;
    margin-bottom: 24px;
  }

  .header-content {
    padding: 0 16px;
  }

  .page-title {
    font-size: 24px;
  }

  .page-subtitle {
    font-size: 14px;
  }

  .card-content {
    padding: 16px;
  }

  .card-header {
    padding: 16px;
  }

  .info-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .modern-textarea {
    min-height: 250px;
  }

  .not-found-card {
    padding: 40px 24px;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 20px;
    flex-direction: column;
    gap: 8px;
  }

  .card-title {
    font-size: 16px;
  }

  .btn-back-modern,
  .btn-save-modern {
    width: 100%;
    text-align: center;
  }

  .actions-section {
    justify-content: stretch;
  }

  .switch-container {
    justify-content: center;
  }

  .modern-form-input {
    font-size: 14px;
    height: 44px;
  }

  .modern-textarea {
    font-size: 14px;
    min-height: 200px;
  }
}
</style>